/**
 * Utility functions for date formatting and manipulation
 * These functions help avoid timezone conversion issues when working with date-only values
 */

/**
 * Formats a Date object as YYYY-MM-DD string in the local timezone
 * This prevents timezone conversion issues when sending date-only values to APIs
 * 
 * @param date - The Date object to format
 * @returns Formatted date string in YYYY-MM-DD format, or null if date is null/undefined
 * 
 * @example
 * formatDateOnly(new Date(2024, 0, 15)) // Returns "2024-01-15"
 */
export function formatDateOnly(date: Date | null | undefined): string | null {
	if (!date) {
		return null;
	}
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');
	return `${year}-${month}-${day}`;
}

/** Local Y-M-D + clock time with no Z/offset. JSON.stringify of Date uses UTC and shifts the day in CST vs IST. */
export function toUnspecifiedDateTime(date: Date, hours: number, minutes: number, seconds: number): string {
	const value = date instanceof Date ? date : new Date(date);
	const year = value.getFullYear();
	const month = String(value.getMonth() + 1).padStart(2, '0');
	const day = String(value.getDate()).padStart(2, '0');
	const hh = String(hours).padStart(2, '0');
	const mm = String(minutes).padStart(2, '0');
	const ss = String(seconds).padStart(2, '0');
	return `${year}-${month}-${day}T${hh}:${mm}:${ss}`;
}

export function toUnspecifiedStartOfDay(date: Date): string {
	return toUnspecifiedDateTime(date, 0, 0, 0);
}

/** 06:00 unspecified — matches API CST convention so Angular date pipe does not roll back a day. */
export function toUnspecifiedCentralStartOfDay(date: Date): string {
	return toUnspecifiedDateTime(date, 6, 0, 0);
}

const ISO_DATE_PREFIX = /^(\d{4})-(\d{2})-(\d{2})/;

/**
 * Calendar Y-M-D from a date-only payload, without converting to the viewer's timezone.
 * ISO strings keep the stored date even when the time is 00:00Z, 06:00, or 23:59:59.999.
 */
export function getCalendarDateParts(value: Date | string | null | undefined): { year: number; month: number; day: number } | null {
	if (value == null || value === '') {
		return null;
	}

	if (typeof value === 'string') {
		const match = ISO_DATE_PREFIX.exec(value.trim());
		if (match) {
			return { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) };
		}
		value = new Date(value);
	}

	const date = value instanceof Date ? value : new Date(value);
	if (isNaN(date.getTime())) {
		return null;
	}

	return {
		year: date.getFullYear(),
		month: date.getMonth() + 1,
		day: date.getDate()
	};
}

/** MM/dd/yyyy from the stored calendar day (no timezone conversion). */
export function formatCalendarDate(value: Date | string | null | undefined): string | null {
	const parts = getCalendarDateParts(value);
	if (!parts) {
		return null;
	}
	const month = String(parts.month).padStart(2, '0');
	const day = String(parts.day).padStart(2, '0');
	return `${month}/${day}/${parts.year}`;
}

/** Local Date at noon of the stored calendar day so month/year filters do not cross midnight. */
export function parseCalendarDate(value: Date | string | null | undefined): Date | null {
	const parts = getCalendarDateParts(value);
	if (!parts) {
		return null;
	}
	return new Date(parts.year, parts.month - 1, parts.day, 12, 0, 0);
}
